// Shared host plumbing for the five brand pages: one engine per resource, password unlock across a package,
// copy-to-clipboard, upload batches. Views receive snapshots and emit intents; only engines change state.
import { forgetOperator, hasOperator, operatorPublishes, operatorToken, requestOperator } from './operator.js';
import { createApi, createDownload, createPeerNetwork, createUpload, defaultFileHasher } from '../sdk/index.js';
import { sanitizeConfig } from './config.js';

export { sanitizeConfig };

let runtimeConfig = {};

/**
 * Optional same-origin runtime config `/hybrid-config.json` (infra-owned; absent → defaults):
 * `{ apiBase?: "/transfer-api/v1", brandHosts?: { rapidshare: "https://…" }, iceServers?: RTCIceServer[] }`.
 * Never contains secrets; unknown or malformed fields are ignored.
 */
export async function loadHostConfig() {
  try {
    const res = await fetch('/hybrid-config.json', { cache: 'no-store', credentials: 'same-origin' });
    if (res.ok && /json/.test(res.headers.get('content-type') || '')) runtimeConfig = sanitizeConfig(await res.json());
  } catch { /* optional */ }
  return hostConfig();
}

export function hostConfig() {
  const w = sanitizeConfig((typeof window !== 'undefined' && window.__SOLRED_HYBRID__) || {});
  const c = { ...w, ...runtimeConfig };
  return { apiBase: c.apiBase || '/transfer-api/v1', brandHosts: c.brandHosts || {}, iceServers: c.iceServers || [] };
}

let sharedPeers = null;
/** One peer network per page: one global upload cap, bounded swarms, consent tracked page-wide. */
export function peers() {
  if (!sharedPeers) sharedPeers = createPeerNetwork({ iceServers: hostConfig().iceServers });
  return sharedPeers;
}

let sharedApi = null;
export function api() {
  if (!sharedApi) sharedApi = createApi({ baseUrl: hostConfig().apiBase });
  return sharedApi;
}

/**
 * Open every resource of the page. Returns `{ state, passwordError, items }` where `state` is the most
 * restrictive gate across the package (nothing about any file is exposed unless all are allowed).
 */
export function createPackage(brand, ids, { onSnapshot } = {}) {
  const items = ids.map((resourceId) => {
    const engine = createDownload({ api: api(), resourceId, brand, fileHasher: defaultFileHasher, onSnapshot, peers: peers() });
    return { resourceId, engine, gate: null };
  });
  const byTransfer = new Map(items.map((it) => [it.engine.transferId, it]));
  const ORDER = ['missing', 'expired', 'denied', 'password-required'];

  async function open(password) {
    const results = await Promise.all(items.map(async (it) => {
      if (it.gate && it.gate.state === 'allowed') return it.gate;
      try { it.gate = await it.engine.open(password); }
      catch (e) { console.error(e); it.gate = { state: 'missing' }; }
      return it.gate;
    }));
    for (const s of ORDER) {
      const hit = results.find((r) => r.state === s);
      if (hit) return { state: s, passwordError: results.some((r) => r.passwordError), items };
    }
    return { state: 'allowed', passwordError: false, items };
  }

  return {
    items,
    open,
    engineFor: (transferId) => byTransfer.get(transferId)?.engine ?? null,
    first: () => items[0]?.engine ?? null,
    destroy: () => items.forEach((it) => it.engine.destroy()),
  };
}

/** Start one upload per selected file; calls `onDone(localId, result)` after the server-confirmed commit. */
export function startUploads(brand, files, { password, onSnapshot, onDone }) {
  return files.map((f) => {
    const u = createUpload({ api: api(), file: f.file, localId: f.localId, name: f.name, brand, password: password || undefined, fileHasher: defaultFileHasher, onSnapshot,
      operatorToken, isPublic: () => !password && operatorPublishes(), onOperatorRejected: () => forgetOperator() });
    u.subscribe((s) => { if (s.status === 'ready' && u.result) onDone(f.localId, u.result); });
    u.start();
    return u;
  });
}

/** The vendor views re-render a gate only when its id or state changes; a failed attempt needs a new id. */
export function gateIds(ids) {
  let attempt = 0;
  return (g) => `gate-${ids.join('-') || 'none'}-${g.passwordError ? ++attempt : 0}`;
}

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

export function pageLinkFor(ids) {
  const u = new URL(location.href);
  u.search = '';
  u.hash = '';
  u.pathname = '/';
  const brand = new URLSearchParams(location.search).get('brand');
  if (brand) u.searchParams.set('brand', brand);
  u.searchParams.set('r', [].concat(ids).join(','));
  return u.toString();
}

export function kindOf(name) {
  const ext = (/\.([a-z0-9]+)$/i.exec(name || '') || [])[1]?.toLowerCase() || '';
  if (/^(png|jpe?g|gif|webp|avif|svg|heic)$/.test(ext)) return 'image';
  if (/^(mp4|webm|mov|m4v|mkv|avi)$/.test(ext)) return 'video';
  if (/^(mp3|wav|ogg|m4a|flac)$/.test(ext)) return 'audio';
  if (/^(pdf|docx?|xlsx?|pptx?|txt|md|csv|json|odt)$/.test(ext)) return 'document';
  if (/^(zip|rar|7z|tar|gz|tgz|bz2|xz)$/.test(ext)) return 'archive';
  return 'other';
}

/** Dispatch a transfer intent coming from any view. Returns true when an engine handled it. */
export function dispatchTransfer(pkg, uploads, a, { lang, needPublish = true } = {}) {
  const e = a.transferId ? pkg.engineFor(a.transferId) : null;
  if (e) return e.dispatch(a);
  const u = uploads.find((x) => x.transferId === a.transferId);
  if (!u) return false;
  // Retrying an upload that failed for lack of (or a refused) credential asks for it again first.
  if (a.type === 'retry' && !hasOperator()) { void requestOperator({ needPublish, lang }).then((op) => op && u.dispatch(a)); return true; }
  return u.dispatch(a);
}
